// The latest forecast on the date axis: how long ago it was made, and the label for the stretch
// between its mark and the Today line, the time the latest forecast still stands for. Pure.
import { APP_FONT_FAMILY, APP_LETTER_SPACING } from "../appFont.js";
import { measureLabelWidth } from "./measureText.js";

/** The gap label's type size, the axis's minor-date size. */
export const GAP_LABEL_FONT = 9;
/** The latest-forecast card's width, which its placement over the axis clamps by. */
export const LATEST_CARD_W = 288;
// Clear space between the label and the mark, the Today line or a date label.
const GAP_LABEL_PAD = 6;

const localDay = (ms) => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/**
 * Calendar days from `iso` to `nowMs` in local time (0 the same day), or null when either
 * does not parse. Never negative.
 * @param {string|null|undefined} iso
 * @param {number} nowMs
 * @returns {number|null}
 */
export function daysSince(iso, nowMs) {
  const ms = Date.parse(String(iso ?? ""));
  if (Number.isNaN(ms) || !Number.isFinite(nowMs)) return null;
  // Rounded, so a daylight-saving day of 23 or 25 hours still counts as one.
  return Math.max(0, Math.round((localDay(nowMs) - localDay(ms)) / 86400000));
}

/** "today", "yesterday", "7 days ago". */
export function agoText(days) {
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

/** @param {string} text */
const labelWidth = (text) =>
  measureLabelWidth(text, {
    fontSize: GAP_LABEL_FONT,
    fontFamily: APP_FONT_FAMILY,
    letterSpacing: APP_LETTER_SPACING,
  });

/**
 * The label for the stretch from the latest mark at `fromX` to the Today line at `toX`: the
 * longest of "Latest forecast · 7 days ago", "7 days ago" and "7d" that fits between them and
 * clears every date label on the axis, centred in the stretch. Null when the latest forecast is
 * from today or nothing fits.
 *
 * @param {{fromX: number, toX: number, days: number|null,
 *   dateLabels?: Array<{x: number, width: number}>, measure?: (text: string) => number}} args
 *   `dateLabels` are the axis's date labels by centre and width; `measure` gives a text's width
 * @returns {{text: string, x: number}|null}
 */
export function latestGapLabel({ fromX, toX, days, dateLabels = [], measure = labelWidth }) {
  if (typeof days !== "number" || days < 1) return null;
  const x = (fromX + toX) / 2;
  const room = toX - fromX - 2 * GAP_LABEL_PAD;
  const ago = agoText(days);
  for (const text of [`Latest forecast · ${ago}`, ago, `${days}d`]) {
    const w = measure(text);
    if (w > room) continue;
    const left = x - w / 2 - GAP_LABEL_PAD;
    const right = x + w / 2 + GAP_LABEL_PAD;
    if (dateLabels.some((d) => d.x + d.width / 2 > left && d.x - d.width / 2 < right)) continue;
    return { text, x };
  }
  return null;
}
